import {
  faPhone,
  faSearch,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useRef, useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { Link, useLocation } from 'react-router-dom';
import './Navbar.css';

const Navbar = () => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchBarIsOpen, setSearchBarIsOpen] = useState(false);
  const [dropdownStates, setDropdownStates] = useState({
    company: false,
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1550);
  const [ModelIsOpen, setModelIsOpen] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [phone, setPhone] = useState('');

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 1550;
      setIsMobile(mobile);
      if (!mobile) {
        // Close all dropdowns when switching to desktop
        setDropdownStates({
          company: false,
        });
        setIsMenuOpen(false); // Close mobile menu on desktop
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    // Close all dropdowns when toggling menu
    setDropdownStates({
      company: false,
    });
  };

  const toggleSearchBar = () => {
    setSearchBarIsOpen(!searchBarIsOpen);
  };

  const toggleModel = () => {
    setModelIsOpen(!ModelIsOpen);
  };

  const toggleDropdown = (dropdownName) => {
    // Only toggle for mobile or when clicking directly on the link in desktop if not hovered
    if (isMobile) {
      setDropdownStates(prev => ({
        ...prev,
        [dropdownName]: !prev[dropdownName]
      }));
    }
  };

  const handleDesktopHover = (dropdownName, isHovering) => {
    if (!isMobile) {
      setDropdownStates(prev => ({
        ...prev,
        [dropdownName]: isHovering
      }));
    }
  };

  const isActive = (path) => location.pathname === path;
  const isCompanyActive = ['/about', '/why-callistus', '/presence'].includes(location.pathname);

  const formRef = useRef();

  const handleModalSubmit = async (e) => {
    e.preventDefault();

    // Make sure phone starts with +
    let formattedPhone = phone.startsWith("+") ? phone : `+${phone}`;

    // General E.164 validation (8 to 15 digits with optional +)
    const intlPhoneRegex = /^\+?[1-9]\d{7,14}$/;

    // UAE-specific validation: +971 followed by 9 digits
    const uaePhoneRegex = /^\+971[0-9]\d{8}$/;

    // UAE-specific check
    if (formattedPhone.startsWith("+971")) {
      if (!uaePhoneRegex.test(formattedPhone)) {
        setShowError(true);
        setTimeout(() => setShowError(false), 4000);
        return;
      }
    } else {
      // International validation
      if (!intlPhoneRegex.test(formattedPhone)) {
        setShowError(true);
        setTimeout(() => setShowError(false), 4000);
        return;
      }
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/send-req-call`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: formattedPhone }), // ✅ send normalized
      });

      const result = await response.json();
      if (result.success) {
        setShowSuccess(true);
        setTimeout(() => {
          setModelIsOpen(false);
          setPhone("");
          setShowSuccess(false);
        }, 4000);
        formRef.current.reset();
      } else {
        console.error("❌ Error:", result.message);
      }
    } catch (error) {
      console.error("❌ Error:", error);
    }
  };

  // TODO: expand this list once all Callistus pages/sub-pages are finalized
  const searchablePages = [
    { name: 'Home', path: '/' },
    { name: 'Company', path: '/about' },
    { name: 'Products', path: '/products' },
    { name: 'Special Solutions', path: '/special-solutions' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Inspiration', path: '/inspiration' },
    { name: 'Career', path: '/career' },
    { name: 'Trade', path: '/trade' },
    { name: 'Contact', path: '/contact' },
  ];


  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query.length > 0) {
      const filteredResults = searchablePages.filter((page) =>
        page.name.toLowerCase().includes(query.toLowerCase())
      );
      setSearchResults(filteredResults);
    } else {
      setSearchResults([]);
    }
  };


  return (
    <>
      <div className="navbar-container">
        <header className="navbar">
          <div className="nav-col1">
            <div className="nav-logo">
              <Link to="/" className="text-logo">
                <img src="/Logo/marvel-logo.png" alt="Callistus Window Fashions" className="brand-logo" />
              </Link>
            </div>
            <div className="mobile-sec2">
              <div className="mobile-searchbar">
                <div className="nav-search-bar" onClick={toggleSearchBar}>
                  <FontAwesomeIcon icon={faSearch} />
                </div>
              </div>
              <div
                className={`navbar-toggle ${isMenuOpen ? 'open-toggle' : ''}`}
                onClick={toggleMenu}
              >
                <span className="toggle-L1"></span>
                <span className="toggle-L2"></span>
                <span className="toggle-L3"></span>
              </div>
            </div>
          </div>

          <div className="nav-col2">
            <ul className={`nav-links ${isMenuOpen ? 'open-nav-links' : ''}`}>
              <li
                className={`company-link ${isMobile ? 'mobile' : 'desktop'} ${dropdownStates.company ? 'open' : ''}`}
                onClick={() => toggleDropdown('company')}
                onMouseEnter={() => handleDesktopHover('company', true)}
                onMouseLeave={() => handleDesktopHover('company', false)}
              >
                <Link to="#" className={isCompanyActive ? 'active-link' : ''}>
                  Company
                </Link>
                <ul className={`dropdown ${dropdownStates.company ? 'open' : ''}`}>
                  <li>
                    <Link to="/about" className={isActive('/about') ? 'active-link' : ''}>About Us</Link>
                  </li>
                  <li>
                    <Link to="/why-callistus" className={isActive('/why-callistus') ? 'active-link' : ''}>Why Callistus</Link>
                  </li>
                  <li>
                    <Link to="/presence" className={isActive('/presence') ? 'active-link' : ''}>Presence</Link>
                  </li>
                </ul>
              </li>

              <li>
                <Link to="/products" className={isActive('/products') ? 'active-link' : ''}>Products</Link>
              </li>

              <li>
                <Link to="/special-solutions" className={isActive('/special-solutions') ? 'active-link' : ''}>Special Solutions</Link>
              </li>

              <li>
                <Link to="/gallery" className={isActive('/gallery') ? 'active-link' : ''}>Gallery</Link>
              </li>

              <li>
                <Link to="/inspiration" className={isActive('/inspiration') ? 'active-link' : ''}>Inspiration</Link>
              </li>

              <li>
                <Link to="/career" className={isActive('/career') ? 'active-link' : ''}>Career</Link>
              </li>

              <li>
                <Link to="/trade" className={isActive('/trade') ? 'active-link' : ''}>Trade</Link>
              </li>

              <li>
                <Link to="/contact" className={isActive('/contact') ? 'active-link' : ''}>Contact</Link>
              </li>
            </ul>
          </div>

          <div className="nav-col3" onClick={toggleSearchBar}>
            <div className="nav-search-bar">
              <FontAwesomeIcon icon={faSearch} />
            </div>
          </div>

          <div className="nav-col4">
            <div className="nav-call-btn" onClick={toggleModel}>
              REQUEST A CALL BACK
              <FontAwesomeIcon icon={faPhone} style={{ marginLeft: '8px' }} />
            </div>
          </div>
        </header>
      </div>

      <div className={`search-container ${searchBarIsOpen ? 'Open-search-container' : ''}`}>
        <input
          type="text"
          className="search-input"
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
        />
        <div className="search-results" style={searchQuery ? { marginTop: "10px" } : {}}>
          {searchQuery && searchResults.length === 0 && (
            <div className="no-results">No results found</div>
          )}
          {searchResults.map((result, index) => (
            <Link
              key={index}
              to={result.path}
              className="search-result-item"
              onClick={() => {
                setSearchBarIsOpen(false);
                setSearchQuery('');
                setSearchResults([]);
              }}
            >
              {result.name}
            </Link>
          ))}
        </div>
      </div>

      <div className={`modal-overlay ${ModelIsOpen ? 'opener' : ''}`}>
        <div className="callback-modal">
          <button className="modal-close-btn" onClick={toggleModel}>
            <FontAwesomeIcon icon={faTimes} />
          </button>

          <div className="modal-title">
            <h5>Request a Call back</h5>
          </div>
          <div className="Model-Bottom">
            <form ref={formRef} onSubmit={handleModalSubmit}>
              <div className="modal-input">
                <PhoneInput
                  country={'ae'}
                  enableSearch={true}
                  value={phone}
                  onChange={(phone) => setPhone(phone)}
                  placeholder="Your Number"
                  inputClass="modal-phone-input" // Add a class for styling
                  containerClass="modal-phone-input-container" // Add a class for styling
                />
              </div>
              {showSuccess && (
                <div className="success-message" style={{ marginTop: '8px' }}>
                  <p>Thank you! We'll call you back shortly.</p>
                </div>
              )}
              {showError && (
                <div className="denger-message" style={{ marginTop: '8px' }}>
                  <p>Please enter a valid phone number.</p>
                </div>
              )}
              <div className="modal-buttons">
                <button type='submit' className="model-submit-btn">
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;